import { useEffect, useRef, useState } from "react";
import L from "leaflet";

/*
 * PropertyMap  -  interactive Leaflet map showing the property location
 * and an animated driving route from the origin (e.g. the airport) to
 * the property. Real route geometry is fetched from OSRM (no API key).
 * Falls back to a straight line if OSRM is unreachable.
 */
const destIcon = L.divIcon({
  className: "",
  html: `<div style="width:36px;height:36px;position:relative;">
    <div style="position:absolute;inset:0;background:#DC2626;border:3px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,0.4);"></div>
    <div style="position:absolute;top:8px;left:8px;width:20px;height:20px;background:white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#050506;transform:rotate(45deg);">📍</div>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

const originIcon = L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;position:relative;">
    <div style="position:absolute;inset:0;background:#050506;border:3px solid white;border-radius:50%;box-shadow:0 4px 10px rgba(0,0,0,0.4);"></div>
    <div style="position:absolute;top:6px;left:6px;width:18px;height:18px;background:#DC2626;border-radius:50%;"></div>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

export function PropertyMap({ property, height = "360px" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const intervalHolder = { current: null };

    // Defer init until the modal animation completes and container has dimensions
    const initTimer = setTimeout(() => {
      if (!containerRef.current || mapRef.current) return;

      const { coordinates: dest, drivingFrom } = property;
      const origin = drivingFrom.coordinates;
      const bounds = L.latLngBounds([
        [origin.lat, origin.lng],
        [dest.lat, dest.lng],
      ]);

      const map = L.map(containerRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true,
      }).fitBounds(bounds.pad(0.25));

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      mapRef.current = map;

      L.marker([origin.lat, origin.lng], { icon: originIcon })
        .addTo(map)
        .bindPopup(`<b>Origin:</b><br/>${drivingFrom.label}`);
      L.marker([dest.lat, dest.lng], { icon: destIcon })
        .addTo(map)
        .bindPopup(`<b>${property.name}</b><br/>${property.location}`);

      const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${dest.lng},${dest.lat}?overview=full&geometries=geojson`;

      fetch(osrmUrl)
        .then((r) => r.json())
        .then((data) => {
          if (!data.routes || !data.routes[0]) throw new Error("no route");
          const route = data.routes[0];
          const latlngs = route.geometry.coordinates.map(
            ([lng, lat]) => ({ lat, lng })
          );

          // Halo
          L.polyline(latlngs, {
            color: "#050506",
            weight: 8,
            opacity: 0.2,
            lineCap: "round",
            lineJoin: "round",
          }).addTo(map);

          // Animated dashed route
          const routeLine = L.polyline(latlngs, {
            color: "#DC2626",
            weight: 5,
            opacity: 0.95,
            dashArray: "8 12",
            lineCap: "round",
            lineJoin: "round",
            className: "route-flow",
          }).addTo(map);

          // Directional arrows
          const arrowCount = Math.min(8, Math.max(3, Math.floor(latlngs.length / 25)));
          const step = Math.floor(latlngs.length / arrowCount);
          for (let i = step; i < latlngs.length - 2; i += step) {
            const p = latlngs[i];
            const next = latlngs[i + 1];
            const angle =
              (Math.atan2(next.lng - p.lng, next.lat - p.lat) * 180) / Math.PI;
            const arrow = L.divIcon({
              className: "",
              html: `<div style="transform:rotate(${angle}deg);font-size:22px;line-height:1;color:#f87171;text-shadow:0 1px 2px rgba(255,255,255,0.8);">➤</div>`,
              iconSize: [22, 22],
              iconAnchor: [11, 11],
            });
            L.marker([p.lat, p.lng], { icon: arrow, interactive: false }).addTo(map);
          }

          // Moving car marker
          const movingIcon = L.divIcon({
            className: "",
            html: `<div style="width:28px;height:28px;background:#050506;border:3px solid #DC2626;border-radius:50%;box-shadow:0 4px 12px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:14px;">🚗</div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
          });
          const movingMarker = L.marker(latlngs[0], { icon: movingIcon }).addTo(map);
          let progress = 0;
          intervalHolder.current = setInterval(() => {
            progress = (progress + 1) % latlngs.length;
            movingMarker.setLatLng([latlngs[progress].lat, latlngs[progress].lng]);
          }, 60);

          map.fitBounds(routeLine.getBounds().pad(0.2));
          setStatus("ready");
        })
        .catch(() => {
          L.polyline(
            [
              [origin.lat, origin.lng],
              [dest.lat, dest.lng],
            ],
            {
              color: "#DC2626",
              weight: 4,
              opacity: 0.85,
              dashArray: "8 12",
              className: "route-flow",
            }
          ).addTo(map);
          setStatus("fallback");
        });
    }, 80);

    return () => {
      clearTimeout(initTimer);
      if (intervalHolder.current) clearInterval(intervalHolder.current);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [property]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{ height, width: "100%", borderRadius: 16, overflow: "hidden" }}
        aria-label="Interactive map showing driving route to the property"
      />
      {status === "loading" && (
        <div className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-medium text-brand shadow-soft-mc flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-coral animate-pulse" />
          Plotting route…
        </div>
      )}
      {status === "fallback" && (
        <div className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-medium text-brand shadow-soft-mc">
          Showing straight-line route
        </div>
      )}
      {status === "ready" && (
        <div className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-medium text-brand shadow-soft-mc">
          Live driving route
        </div>
      )}
    </div>
  );
}
